/*
  Lawless storefront analytics — cart instrumentation + identity relay.

  Two jobs, both of which the storefront was missing entirely (the only
  posthog.capture calls in the theme were the beta gate's):

  1. add_to_cart / cart_updated events. Deliberately reuses the SAME event name
     the configurator already sends; the `surface` super property registered in
     snippets/posthog.liquid ('storefront') vs the configurator's `app`
     ('configurator') is what tells them apart. One name, one funnel.

  2. Identity relay into checkout. Shopify's checkout runs outside the theme, so
     a purchase would otherwise land as a brand-new anonymous person and the
     browsing session that led to it stays orphaned. Writing PostHog's ids into
     Shopify cart attributes carries them into the order, where both the web
     pixel (checkout.attributes) and the Admin API order payload can read them.
     This path does not depend on web-pixel sandbox behaviour, so it works even
     if browser.cookie.get()/localStorage access changes.

  3. Page-level commerce events: product_viewed, collection_viewed,
     cart_viewed, search_performed and checkout_begun. The storefront funnel
     previously jumped straight from "$pageview" to "add_to_cart" with nothing
     in between, so browse-to-cart drop-off — the biggest, most actionable gap
     on the site — was impossible to see. Page shape comes from
     window.LW_PAGE, built by Liquid in snippets/posthog.liquid.

  Hooks the theme's own pub/sub ('cart-update') and DOM event ('cart:updated')
  rather than patching window.fetch — safer on a live store.
*/
(function () {
  'use strict';

  var ATTR_STAMP_KEY = 'lawless.cart.attrs.v1';

  // Latest cart totals we've observed, so checkout_begun can report what was
  // in the cart at the moment someone left for checkout.
  var lastCart = null;

  function ph() {
    return (window.posthog && typeof window.posthog.capture === 'function') ? window.posthog : null;
  }

  function ids() {
    var p = ph();
    if (!p) return null;
    try {
      // No consent (or opted out): keep the analytics id out of the cart and
      // order too, not just out of PostHog. See the consent gate in posthog.liquid.
      if (typeof p.has_opted_out_capturing === 'function' && p.has_opted_out_capturing()) return null;
      var distinctId = typeof p.get_distinct_id === 'function' ? p.get_distinct_id() : null;
      if (!distinctId) return null;
      return {
        distinctId: distinctId,
        sessionId: typeof p.get_session_id === 'function' ? (p.get_session_id() || '') : ''
      };
    } catch (e) { return null; }
  }

  // Push the PostHog ids onto the Shopify cart so they survive into the order.
  // Deduped per session so a busy cart doesn't spam /cart/update.js.
  function relayIdsToCart() {
    var id = ids();
    if (!id) return;

    var stamp = id.distinctId + '|' + id.sessionId;
    try { if (sessionStorage.getItem(ATTR_STAMP_KEY) === stamp) return; } catch (e) {}

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        attributes: {
          ph_distinct_id: id.distinctId,
          ph_session_id: id.sessionId
        }
      })
    }).then(function (res) {
      if (!res.ok) return;
      try { sessionStorage.setItem(ATTR_STAMP_KEY, stamp); } catch (e) {}
    }).catch(function () { /* never let analytics break the cart */ });
  }

  function money(cents) {
    return (typeof cents === 'number') ? cents / 100 : undefined;
  }

  function captureAddToCart(line) {
    var p = ph();
    if (!p || !line) return;
    try {
      p.capture('add_to_cart', {
        product_title: line.product_title || line.title,
        variant_title: line.variant_title,
        variant_id: line.variant_id || line.id,
        product_id: line.product_id,
        sku: line.sku,
        quantity: line.quantity,
        price: money(line.price),
        line_price: money(line.line_price),
        currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || undefined
      });
    } catch (e) {}
  }

  function captureCartTotals(cart, reason) {
    var p = ph();
    if (!p || !cart) return;
    try {
      p.capture('cart_updated', {
        reason: reason,
        item_count: cart.item_count,
        total_price: money(cart.total_price),
        currency: cart.currency
      });
    } catch (e) {}
  }

  // Sources that mean "a line was ADDED", as opposed to a quantity edit or a
  // removal. quick-add-bulk was previously misfiled as a plain cart_updated,
  // so bulk adds never showed up in the funnel at all.
  var ADD_SOURCES = ['product-form-component', 'quick-add-bulk'];

  function looksLikeLine(d) {
    return Boolean(d && (d.variant_id || d.id) && (d.product_title || d.title));
  }

  function onCartUpdate(payload) {
    if (!payload) return;
    var data = payload.cartData;
    if (ADD_SOURCES.indexOf(payload.source) !== -1 && looksLikeLine(data)) {
      captureAddToCart(data);
    } else if (data && typeof data.item_count === 'number') {
      lastCart = data;
      captureCartTotals(data, payload.source || 'unknown');
    }
    relayIdsToCart();
  }

  /*
    Page-level events, driven by window.LW_PAGE (see snippets/posthog.liquid).
    One event per page load, chosen by template — these are the middle of the
    storefront funnel that was missing entirely.
  */
  function capturePageView() {
    var p = ph();
    var ctx = window.LW_PAGE;
    if (!p || !ctx) return;
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || undefined;
    try {
      if (ctx.product) {
        p.capture('product_viewed', {
          product_id: ctx.product.id,
          product_title: ctx.product.title,
          product_handle: ctx.product.handle,
          product_type: ctx.product.type,
          vendor: ctx.product.vendor,
          price: ctx.product.price,
          compare_at_price: ctx.product.compare_at_price || undefined,
          available: ctx.product.available,
          variant_count: ctx.product.variant_count,
          collections: ctx.product.collections,
          currency: currency
        });
      } else if (ctx.collection) {
        p.capture('collection_viewed', {
          collection_id: ctx.collection.id,
          collection_title: ctx.collection.title,
          collection_handle: ctx.collection.handle,
          product_count: ctx.collection.product_count
        });
      } else if (ctx.cart) {
        lastCart = { item_count: ctx.cart.item_count, total_price: ctx.cart.total_price * 100 };
        p.capture('cart_viewed', {
          item_count: ctx.cart.item_count,
          total_price: ctx.cart.total_price,
          item_handles: ctx.cart.item_handles,
          currency: currency
        });
      } else if (ctx.search) {
        p.capture('search_performed', {
          query: ctx.search.terms,
          results_count: ctx.search.results_count,
          zero_results: ctx.search.results_count === 0
        });
      }
    } catch (e) {}
  }

  /*
    Leaving for checkout. The Shopify web pixel fires checkout_started once
    Shopify's own checkout loads, but that is a different origin and a
    different script — if it ever breaks, or the shopper bounces before it
    boots, the theme-side intent is the last thing we can still see. Cheap
    insurance, and it makes cart -> checkout drop-off measurable from one side.

    sendBeacon because a full navigation follows immediately.
  */
  function captureCheckoutBegun(via) {
    var p = ph();
    if (!p) return;
    try {
      p.capture('checkout_begun', {
        via: via,
        item_count: lastCart ? lastCart.item_count : undefined,
        total_price: lastCart ? money(lastCart.total_price) : undefined,
        currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || undefined
      }, { transport: 'sendBeacon' });
    } catch (e) {}
  }

  // Shopify's own "View in your space" button on product media (Quick Look on
  // iPhone, Scene Viewer on Android). Same event as the Builder's AR button;
  // the phone takes over after the tap, so the tap is all we can see.
  function captureNativeAR(btn) {
    var p = ph();
    if (!p) return;
    var ctx = window.LW_PAGE;
    try {
      p.capture('ar_launch_tapped', {
        surface: 'product',
        product: ctx && ctx.product ? ctx.product.handle : undefined,
        product_title: btn.getAttribute('data-shopify-title') || undefined,
        model_id: btn.getAttribute('data-shopify-model3d-id') || undefined
      }, { transport: 'sendBeacon' });
    } catch (e) {}
  }

  // Every route to checkout the theme offers: the cart page's submit button,
  // the drawer's, and any direct link.
  var CHECKOUT_SELECTOR = [
    '[name="checkout"]',
    'a[href="/checkout"]',
    'a[href^="/checkout?"]',
    '.cart__checkout-button',
    '[data-checkout-button]'
  ].join(',');

  /*
    Identify from ANY Shopify contact-form submission that carries an email.
    That single hook covers the footer newsletter, the contact page, Ask an
    expert, the back-in-stock notify form and the Speedy build quote form —
    every place on the storefront where someone hands over an email.

    Canonical distinct_id is the lowercased email, matching the checkout pixel
    and snippets/posthog.liquid. Sent with sendBeacon because these forms do a
    full-page POST and a batched event would be lost in the navigation.
  */
  function identifyFromForm(form) {
    var p = ph();
    if (!p || !form) return;

    var emailInput =
      form.querySelector('input[name="contact[email]"]') ||
      form.querySelector('input[type="email"]');
    if (!emailInput || !emailInput.value) return;

    var email = String(emailInput.value).trim().toLowerCase();
    if (email.indexOf('@') === -1) return;

    var formType = (form.querySelector('input[name="form_type"]') || {}).value || 'contact';
    var nameInput = form.querySelector('input[name="contact[name]"]');
    var phoneInput = form.querySelector('input[name="contact[phone]"]');

    var props = { email: email, $email: email };
    if (nameInput && nameInput.value) props.name = nameInput.value.trim();
    if (phoneInput && phoneInput.value) props.phone = phoneInput.value.trim();

    try {
      p.identify(email, props);
      p.capture('lead_submitted', {
        form_type: formType,
        form_id: form.id || undefined,
        page: window.location.pathname
      }, { transport: 'sendBeacon' });
    } catch (e) {}
  }

  function init() {
    // capture phase so we still run if a handler calls stopPropagation
    document.addEventListener('submit', function (e) {
      if (e && e.target) identifyFromForm(e.target);
    }, true);

    // Capture phase again: checkout buttons live inside forms that navigate.
    document.addEventListener('click', function (e) {
      if (!e || !e.target || typeof e.target.closest !== 'function') return;
      var hit = e.target.closest(CHECKOUT_SELECTOR);
      if (hit) captureCheckoutBegun(hit.getAttribute('name') === 'checkout' ? 'cart-button' : 'link');
      var ar = e.target.closest('[data-shopify-xr]');
      if (ar) captureNativeAR(ar);
    }, true);

    if (typeof window.subscribe === 'function') {
      window.subscribe('cart-update', onCartUpdate);
    }
    // cart.js dispatches this one with the full cart as detail
    document.addEventListener('cart:updated', function (e) {
      if (e && e.detail && typeof e.detail.item_count === 'number') {
        lastCart = e.detail;
        captureCartTotals(e.detail, 'cart:updated');
      }
      relayIdsToCart();
    });

    capturePageView();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
